// The RANKS panel of the hub (R229).
//
// The panel holds six boards (R1737): HONOUR, CLANS, SEASONS, MEMORIALS,
// ACHIEVEMENTS and ARCADE LADDER. Under HONOUR a toggle row CORE · EVENT ·
// CLAN WAR picks one of three account ladders. They stay three boards and
// never one: an event world and a Clan War world each write their own
// Honour, and the figures must not add up (R1512). SEASONS puts the season
// table of the account's own seats over the closed seasons of every world.
//
// **The section shell** (`../sectionshell.ts`, ui-map-interface.md §16.3
// and §16.16, card 02 of mockup round 092, R1903). Each board is one
// section of the rail and one tile; the HONOUR toggle stays a filter row
// inside its page, because a filter is never a rail row. Each row and tile
// states the reader's own place on that board. The places come from ONE
// read, `/api/leaderboards/me` (R1907), which the panel starts on its
// first open and on the tiles; a summary PEEKS at the read of a board and
// never starts one. Where neither has answered, a summary states the
// figure the account carries (its Honour, its clan tag, its seat), or
// nothing. The companion column YOU ON THIS BOARD shows the reader's row,
// the places on each side of it and the gap to the next place, with
// ▸ RECORD. Under the room for the column, and on a telephone, it folds to
// a pinned foot whose press scrolls the board to the reader's row. A
// telephone shows the YOU card under the tiles.
//
// What it reads:
//
//   `/api/leaderboards/honour?mode=core`   the lifetime Honour ladder.
//   `/api/leaderboards/honour?mode=event`  the event ladder, kept apart.
//   `/api/leaderboards/honour?mode=clanwar`
//                                          the Clan Honour ladder of
//                                          accounts, kept apart (R1512).
//   `/api/leaderboards/clans`              the Clan Honour ladder of
//                                          clans: the clan ledger (R1512).
//   `/api/leaderboards/season?world=&season=`
//                                          one world's closed season. The
//                                          route needs a world and a
//                                          season, so the panel offers a
//                                          filter for each seat the account
//                                          holds (R223 placements).
//   `/api/leaderboards/history`            the closed seasons, newest
//                                          first. A card shows the Honour
//                                          that season paid, which the
//                                          server sums from its ledger
//                                          (R1599); the live ladders
//                                          above stay lifetime. The
//                                          payload is the server's own
//                                          board, so the panel reads it
//                                          defensively.
//   `/api/leaderboards/memorials`          the memorial pool: the winners
//                                          of every world, newest season
//                                          first (numbers.md §14.1, R1243).
//   `/api/leaderboards/me`                 the reader's own place on every
//                                          board but MEMORIALS and
//                                          ACHIEVEMENTS, in one answer
//                                          (R1907). The arcade ladder
//                                          states the tier, never the
//                                          number (R1619).
//   `/api/leaderboards/achievements?board=&tier=`
//                                          the achievement points board
//                                          (achievements.md §7). Two
//                                          toggles pick one of its four
//                                          faces: LIFETIME or SEASON, and
//                                          FREE or PREMIUM.
//
// The account itself comes from `ctx.me()`, which the shell reads.
//
// What is honestly empty today:
//
//   - An open season has no closed report. The season route answers 404
//     `not_found` for one, and the panel prints "THIS SEASON HAS NOT
//     CLOSED YET" rather than a failure.
//   - An account with no placement gets no season table at all, because
//     the route has no world to ask about. SEASONS then shows the closed
//     seasons alone.
//   - No season has closed on a new deployment, so the history is an empty
//     list and the panel says so.
//   - An account that is not on the page it drew still gets its own row,
//     with its place from `/api/leaderboards/me` and the Honour the
//     account carries. The row says UNRANKED where the board holds no row
//     of the account.

import { button, filterChoice, linkButton, tabStrip, type Tab } from '../../choice/choice';
import {
  ApiError, CODE_NOT_FOUND, getAchievementBoard, getBoardHistory,
  getBoardMemorials, getClanHonourBoard, getHonourBoard, getLadderList, getMyPlaces, getSeasonBoard,
  type AchievementBoard, type AchievementBoardKind, type AchievementTier,
  type BoardSnapshot, type ClanHonourBoard, type HonourBoard, type LadderCard, type LadderList,
  type Me, type MemorialEntry, type MyPlaces, type Placement, type SeasonBoard,
} from '../../../net/api';
import { factionColor, factionName, hex } from '../../../world';
import { MODE_CORE } from '../../../gen/balance';
import { ladder as L } from '../../../gen/strings';
import { inkOf, ladderMark, tierName, tierOf as ladderTierOf } from '../../ladder';
import { ARCADE_HREF, ARCADE_LADDER_HREF } from '../../hublink';
import type { SfxId } from '../../../audio/triggers';
import {
  formatAgo, groupDigits, ordinal, TTL_LIVE, TTL_ON_OPEN, type Cell,
} from '../hubstate';
// The card's row count rides presentation.toml [presentation.chrome].
import { CHROME_HISTORY_NAMES, CHROME_MEMORIALS_SHOWN, HUB_SECTIONS } from '../../../gen/presentation';
import { hubRanks as S, achievements as A } from '../../../gen/strings';
import {
  card, col, empty, esc, figLine, h1, lede, pending, ctlRow, plain, RANKS_BOARDS, readBoard, rows,
  section, tabData, type PageKey, type Panel, type PanelContext, type RanksBoard, type Row,
} from '../panel';
import {
  SectionNav, shellHtml, shellPress, type Companion, type ShellPage, type ShellSection,
} from '../sectionshell';


/** Which board the rail picked: one of `RANKS_BOARDS` of `panel.ts`. */
type Board = RanksBoard;

/** The Honour ladders, one per Honour column (R1512). */
type HonourMode = 'core' | 'event' | 'clanwar';

/**
 * The board and the HONOUR toggle one board key names, or undefined for a
 * key the panel does not know. The keys of the nine boards before R1737
 * stay aliases: `event` and `clanwar` open HONOUR on that toggle, and
 * `season` and `history` open SEASONS.
 */
export function boardOf(key: string): { board: Board; mode?: HonourMode } | undefined {
  switch (key) {
    case 'honour': case 'clans': case 'seasons': case 'memorials': case 'achievements': case 'ladder':
      return { board: key };
    case 'core': case 'event': case 'clanwar':
      return { board: 'honour', mode: key };
    case 'season': case 'history':
      return { board: 'seasons' };
    default:
      return undefined;
  }
}

/** The size of the ladder mark beside a name on the ARCADE LADDER list. */
const LADDER_MARK_PX = 16;

/** One line of the ARCADE LADDER list: the place, the mark, the name and
 *  the banner (round 078 card 05). It prints no ladder number. */
function ladderLine(place: string, tier: number, name: string, side: string, cls: string): string {
  return `<div class="lrk${cls === '' ? '' : ` ${cls}`}"><span class="n">${esc(place)}</span>`
    + `${ladderMark(tier, LADDER_MARK_PX)}<span class="nm">${esc(name)}</span>${side}</div>`;
}

/** The banner of a row, in its own colour, or nothing before the roll. */
function bannerTag(faction: number): string {
  if (faction <= 0) return '<span class="b"></span>';
  return `<span class="b" style="color:${hex(factionColor(faction))}">`
    + `${esc(factionName(faction).toUpperCase())}</span>`;
}

/** The colour class of a ladder line: the account's own row wears the
 *  accent, and the first three places wear gold. */
function rowCls(self: boolean, place: number): string {
  return self ? 'acc' : place <= 3 ? 'gold' : '';
}

/** One ladder line. `self` gives it the highlight of `hub.css`. With
 *  `seasons` it is a line of the HONOUR board on a wide pane: the place
 *  and the name, the seasons the account played, and the Honour (card 02
 *  of mockup round 092, R1907). The seasons ride the row of the ladder
 *  itself, so the column costs no read; an empty `seasons` leaves the cell
 *  blank. */
function ladderRow(place: string, name: string, value: string, cls: string, self: boolean, seasons?: string): string {
  return `<div class="row${seasons === undefined ? '' : ' rk-sn'}${self ? ' self' : ''}">`
    + `<span class="k">${S.ladder_row({ place: esc(place), name: esc(name) })}</span>`
    + (seasons === undefined ? '' : `<span class="sn">${esc(seasons)}</span>`)
    + `<span class="v${cls === '' ? '' : ` ${cls}`}">${esc(value)}</span></div>`;
}

/** The Honour the account carries on one ladder (R1512). */
function ownHonour(me: Me, mode: HonourMode): number {
  return mode === 'core' ? me.account.honourCore
    : mode === 'event' ? me.account.honourEvent : me.account.honourClanwar ?? 0;
}

/** The key of the reader's places on every board (R1907). */
const PLACES_KEY = 'ranks:me';

/** The head of the wide HONOUR board: it names the two figure columns. */
function seasonsHead(): string {
  return `<div class="row rk-sn rk-hd"><span class="k">${esc(S.col_name)}</span>`
    + `<span class="sn">${esc(S.col_seasons)}</span><span class="v">${esc(S.col_honour)}</span></div>`;
}

/** The rows a history card carries, where it carries any. The payload is
 *  the server's own board and not a wire type, so every field is checked
 *  before it is read. */
function snapshotNames(payload: unknown): { rank: number; name: string; honour: number }[] {
  if (typeof payload !== 'object' || payload === null) return [];
  const raw = (payload as { rows?: unknown }).rows;
  if (!Array.isArray(raw)) return [];
  const out: { rank: number; name: string; honour: number }[] = [];
  for (const r of raw) {
    if (typeof r !== 'object' || r === null) continue;
    const o = r as { rank?: unknown; name?: unknown; honour?: unknown };
    const name = typeof o.name === 'string' ? o.name : '';
    if (name === '') continue;
    out.push({
      rank: typeof o.rank === 'number' ? o.rank : out.length + 1,
      name,
      honour: typeof o.honour === 'number' ? o.honour : 0,
    });
    // A history card is a summary and not a second ladder.
    if (out.length >= CHROME_HISTORY_NAMES) break;
  }
  return out;
}

/** True when a failure is the 404 an open season answers with. */
function isNotFound(err: unknown): boolean {
  return err instanceof ApiError && (err.code === CODE_NOT_FOUND || err.status === 404);
}

/** The key one placement's season board is cached under. */
function seasonKey(p: Placement): string {
  return `${p.worldId}:${p.season}`;
}

/** The seats of the account, the core world first (R1737). The sort is
 *  stable, so the seats of one rank keep the order the server sent. */
function seatsCoreFirst(list: readonly Placement[]): Placement[] {
  const rank = (p: Placement) => (p.mode === MODE_CORE ? 0 : 1);
  return [...list].sort((a, b) => rank(a) - rank(b));
}

/** The six sections of RANKS, in the rail order: the six boards of R1737.
 *  The ids are the address words, `?tab=ranks&section=clans`, and the board
 *  keys of `ctx.go('ranks', board)`. */
export const RANKS_SECTIONS = RANKS_BOARDS;

/** The old board keys of the nine pills before R1737, which stay address
 *  words: `?tab=ranks&section=event` opens HONOUR on EVENT. */
const ALIASES: Readonly<Record<string, Board>> = {
  core: 'honour', event: 'honour', clanwar: 'honour', season: 'seasons', history: 'seasons',
};

/** The press of a rail row and of a tile, ‹ RANKS of a narrow page, and
 *  the pinned foot that scrolls the board to the reader's row. */
const GO = 'rk-sec';
const BACK = 'rk-back';
const SHELL = { go: GO, back: BACK };
const MY_ROW = 'rk-myrow';

/** One line of a board, as the YOU ON THIS BOARD column reads it. */
export interface BoardLine {
  place: number;
  name: string;
  value: number;
  self: boolean;
}

/** The reader's standing on one board: the own line, the places on each
 *  side of it, and the gap to the line above it. */
export interface Standing {
  line: BoardLine;
  near: BoardLine[];
  /** Undefined on the first line of the page. */
  gap?: number;
}

/**
 * The reader's standing on the lines one board returned, with `n` places
 * on each side; undefined where the lines hold no own line. The gap is
 * what the line above holds more than the own line.
 */
export function standingOf(lines: readonly BoardLine[], n: number): Standing | undefined {
  const at = lines.findIndex((l) => l.self);
  if (at < 0) return undefined;
  const line = lines[at]!;
  const above = at > 0 ? lines[at - 1] : undefined;
  return {
    line,
    near: lines.slice(Math.max(0, at - n), at + n + 1),
    gap: above === undefined ? undefined : above.value - line.value,
  };
}

/**
 * What the shell knows of one board without a read of its own.
 *
 *   - `cell`: the state of the board's read, from a peek.
 *   - `lines`: the lines, or undefined while the read has not answered.
 *   - `fmt`: the form of a value; undefined where the board prints none
 *     (the ARCADE LADDER list, as the owner ruled for R1619).
 *   - `own`: the figure the account carries without the board (its
 *     Honour), or undefined.
 *   - `place`: the reader's place where the read states it beside the
 *     lines (the ARCADE LADDER list), or undefined.
 *   - `note`: a sentence in place of a standing (no clan, a guest, an open
 *     season), with an optional forward press.
 */
interface BoardView {
  cell: Cell<unknown>;
  lines?: BoardLine[];
  fmt?: (v: number) => string;
  own?: string;
  place?: number;
  note?: string;
  press?: string;
}

export function createRanksPanel(ctx: PanelContext): Panel {
  /** The open section and the section the address named (§16.16). */
  const nav = new SectionNav({ ids: () => RANKS_SECTIONS, first: () => 'honour', aliases: ALIASES });
  /** Which Honour ladder the HONOUR toggle row picked. */
  let honourMode: HonourMode = 'core';
  /** Which placement's season the panel draws. An empty string means "the
   *  first placement the account holds". */
  let season = '';
  /** Which face of the achievement board the two toggles picked. */
  let achBoard: AchievementBoardKind = 'life';
  let achTier: AchievementTier = 'premium';

  /** The reader's place on every board, from ONE read (R1907): `html`
   *  starts it on the first open of RANKS and on the tiles, and every
   *  other reader peeks at it. */
  const places = (): MyPlaces | undefined => ctx.data.peek<MyPlaces>(PLACES_KEY).value;

  /** A place of the read, or undefined where the board holds no row of
   *  the reader or the read has not answered. */
  const placeOr = (n: number | undefined): number | undefined => (n === undefined || n <= 0 ? undefined : n);

  /** Asks the section one key names, and takes the HONOUR toggle an old
   *  key names (`event` opens HONOUR on EVENT). A key the panel does not
   *  know changes nothing. */
  function ask(key: string): void {
    if (!nav.ask(key)) return;
    const mode = boardOf(key)?.mode;
    if (mode !== undefined) honourMode = mode;
  }

  /**
   * The ARCADE LADDER list (round 078 card 05, arcade.md §18.6).
   *
   * The list runs from the top, grouped under the tier names, and each
   * tier heads its group with the count of the whole tier (R1619). It
   * prints no number, as the owner ruled. The reader's own row wears the
   * highlight where the page holds it, and it is pinned under the list
   * where the page does not. The LADDER section of the arcade shows the
   * top of this list with the reader's own card (R1948).
   */
  function ladderBoard(): string {
    const cell = ctx.data.cell<LadderList>('ladder:list', TTL_ON_OPEN, () => getLadderList());
    const b = cell.value;
    if (b === undefined) return plain(figLine(L.ranks_more) + pending(cell));
    let body = '';
    let group = 0;
    let onPage = false;
    for (const r of b.rows) {
      const t = ladderTierOf(r.ladder);
      if (t !== group) {
        group = t;
        body += `<div class="row lgroup"><span class="k" style="color:${inkOf(t)}">`
          + `${esc(tierName(t))}</span><span class="v">${esc(L.ranks_count({ n: b.tiers[t - 1] ?? 0 }))}`
          + '</span></div>';
      }
      if (r.self) onPage = true;
      body += ladderLine(groupDigits(r.place), t, r.self ? L.ranks_you({ name: r.name }) : r.name,
        bannerTag(r.faction), r.self ? 'me' : '');
    }
    if (b.rows.length === 0) {
      body = empty(L.ranks_empty, L.ranks_empty_why({ days: b.activeDays }),
        { label: S.ladder_arcade_press, href: ARCADE_HREF });
    }
    const me = b.me;
    if (me !== null && me.guest) {
      body += `<div class="lede lpin">${esc(L.guest_line)}</div>`;
    } else if (me !== null && !onPage) {
      const t = ladderTierOf(me.ladder);
      body += ladderLine(me.place > 0 ? groupDigits(me.place) : L.ranks_unplaced, t,
        L.ranks_you({ name: ctx.me().account.name }),
        `<span class="b">${esc(tierName(t))}</span>`, 'me gap');
    }
    // The list is every commander's; the reader's own number stands in
    // the LADDER section of the arcade, one press away (R1948).
    const own = me !== null && !me.guest
      ? ctlRow(linkButton(L.ranks_own, ARCADE_LADDER_HREF, { variant: 'primary' })) : '';
    return plain(figLine(L.ranks_more) + body + `<div class="lede lnote">${esc(L.ranks_note)}</div>` + own);
  }

  /**
   * The achievement points board (achievements.md §7).
   *
   * Two toggles, four faces. LIFETIME and SEASON are two boards the service
   * snapshots apart, and FREE and PREMIUM are the two halves of each one:
   * Premium gates the event mode, and the event category holds the separate
   * counters of that gated area, so one board of both tiers would compare
   * two different games. Supporter shares the free board.
   *
   * The cell is `TTL_LIVE` and not `TTL_ON_OPEN`: a reader who switches a
   * toggle expects the figures under it, and four faces at five minutes
   * each would show a stale page after one change of the tier.
   */
  function achievementCard(): string {
    // Two filters of one board, so two tab strips (R2011).
    const toggles = ctlRow(
      tabStrip([{ id: 'life', label: A.board_life }, { id: 'season', label: A.board_season }], achBoard, 'achboard')
      + tabStrip([{ id: 'free', label: A.board_free }, { id: 'premium', label: A.board_premium }], achTier, 'achtier'));
    const cell = ctx.data.cell<AchievementBoard>(
      `achboard:${achBoard}:${achTier}`, TTL_LIVE,
      () => getAchievementBoard(achBoard, achTier));
    const b = cell.value;
    // A meta server with no progress block answers 503. The board says so
    // rather than drawing an empty ladder, which would read as "nobody
    // has any points" and be a lie.
    if (b === undefined) return toggles + plain(pending(cell));
    let body = '';
    for (const r of b.rows) {
      body += ladderRow(ordinal(r.place), r.name,
        A.record_points({ points: groupDigits(r.points) }), rowCls(r.self, r.place), r.self);
    }
    if (body === '') body = empty(A.board_empty, A.progress_down);
    return toggles + plain(figLine(A.board_taken({ when: formatAgo(ctx.now(), b.takenAt) })) + body);
  }

  /** The HONOUR board: the toggle row CORE · EVENT · CLAN WAR, then the
   *  one ladder it picked. Each ladder keeps its own read and its own
   *  title, so the three never add up (R1512, R1737). */
  function honourBoard(): string {
    const modes: Tab[] = [
      { id: 'core', label: S.toggle_core }, { id: 'event', label: S.toggle_event },
      { id: 'clanwar', label: S.toggle_clanwar },
    ];
    return ctlRow(tabStrip(modes, honourMode, 'honourmode', { label: S.toggle_label })) + honourCard(honourMode);
  }

  /** One Honour ladder: core, event or Clan Honour. */
  function honourCard(mode: HonourMode): string {
    const me = ctx.me();
    const cell = ctx.data.cell<HonourBoard>(
      `honour:${mode}`, TTL_ON_OPEN, () => getHonourBoard(mode));
    const title = { core: S.title_honour, event: S.title_event, clanwar: S.title_clanwar }[mode];
    const b = cell.value;
    if (b === undefined) return plain(figLine(title) + pending(cell));

    // A wide pane adds the SEASONS column (card 02 of round 092, R1907).
    const wide = ctx.wide();
    const line = (place: string, name: string, seasons: string, value: string, cls: string, self: boolean) =>
      ladderRow(place, name, value, cls, self, wide ? seasons : undefined);
    let onPage = false;
    let body = '';
    for (const r of b.rows) {
      const self = r.accountId === me.account.id;
      if (self) onPage = true;
      body += line(ordinal(r.rank), r.name, groupDigits(r.seasons),
        S.honour_value({ honour: groupDigits(r.honour) }), rowCls(self, r.rank), self);
    }
    // An empty board states its empty line and no own row: the YOU column
    // states the reader's Honour once (R2047, R2146).
    const isEmpty = body === '';
    if (isEmpty) {
      body = empty(S.ladder_empty, S.ladder_empty_why, { label: S.ladder_empty_press, tab: 'play' });
    } else if (wide) {
      body = seasonsHead() + body;
    }
    if (!onPage && !isEmpty) {
      // The account is not on the page this call returned. Its own Honour
      // is still known, and the place comes from the read of R1907.
      const place = placeOr(places()?.honour[mode].place);
      body += line(place === undefined ? S.unranked : ordinal(place), S.unranked_you, '',
        S.honour_value({ honour: groupDigits(ownHonour(me, mode)) }), 'acc', true);
    }
    return plain(figLine(`${title} · ${formatAgo(ctx.now(), b.takenAt)}`) + body);
  }

  /** The Clan Honour ladder of clans (R1512). The account's own clan wears
   *  the highlight, and a clan that is not on the page still gets its own
   *  row, as an account does on an Honour ladder. */
  function clanCard(): string {
    const mine = ctx.me().clan ?? null;
    const cell = ctx.data.cell<ClanHonourBoard>('clans', TTL_ON_OPEN, () => getClanHonourBoard());
    const b = cell.value;
    if (b === undefined) return plain(figLine(S.title_clans) + pending(cell));

    let onPage = false;
    let body = '';
    for (const r of b.rows) {
      const self = mine !== null && r.clanId === mine.id;
      if (self) onPage = true;
      body += ladderRow(ordinal(r.rank), S.clan_row({ tag: r.tag, name: r.name }),
        S.honour_value({ honour: groupDigits(r.honour) }), rowCls(self, r.rank), self);
    }
    if (body === '') body = empty(S.clans_empty, S.clans_empty_why, { label: S.clans_empty_press, tab: 'clan', section: 'find' });
    if (mine !== null && !onPage) {
      const own = places()?.clan;
      const place = own?.clanId === mine.id ? placeOr(own.place) : undefined;
      body += ladderRow(place === undefined ? S.unranked : ordinal(place), S.clan_row({ tag: mine.tag, name: mine.name }),
        S.honour_value({ honour: groupDigits(mine.honourClanwar ?? 0) }), 'acc', true);
    }
    return plain(figLine(`${S.title_clans} · ${formatAgo(ctx.now(), b.takenAt)}`) + body);
  }

  /** The seat whose season SEASONS draws: the picked one, else the first,
   *  the core world first; undefined for an account with no seat. */
  function chosenSeat(): Placement | undefined {
    const seats = seatsCoreFirst(ctx.me().placements);
    return seats.find((p) => seasonKey(p) === season) ?? seats[0];
  }

  /** The closed table of one world's season as a section of SEASONS, the
   *  seat filter of the account under its sub-header, the core world
   *  first. An account with no seat gets nothing here, and SEASONS shows
   *  the closed seasons alone (R1737, R1945). */
  function seasonCard(): string {
    const me = ctx.me();
    const seats = seatsCoreFirst(me.placements);
    const chosen = chosenSeat();
    if (chosen === undefined) return '';
    // The seats are a filter of the board and their count grows, so the
    // chooser takes a tab strip to four and a select box past it (R2011).
    const row = seats.length === 1 ? '' : ctlRow(filterChoice({
      list: seats.map((p) => ({ id: seasonKey(p), label: S.season_label({ world: p.world, season: p.season }) })),
      chosen: seasonKey(chosen), act: 'season', uid: 'ranks-season', label: S.board_seasons,
    }));

    const title = S.season_label({ world: chosen.world, season: chosen.season });
    const cell = ctx.data.cell<SeasonBoard>(
      `season:${seasonKey(chosen)}`, TTL_ON_OPEN,
      () => getSeasonBoard(chosen.worldId, chosen.season));
    const b = cell.value;
    if (b === undefined) {
      // An open season has no stored report, and the route says so with a
      // 404. That is a state and not a failure.
      const inner = isNotFound(cell.error)
        ? empty(S.season_open, S.season_open_why)
        : pending(cell);
      return section(title, '', row + inner);
    }

    let body = '';
    for (const r of b.rows) {
      const self = r.accountId === me.account.id;
      body += ladderRow(ordinal(r.rank), r.name, groupDigits(r.score), rowCls(self, r.rank), self);
    }
    if (body === '') body = empty(S.season_empty, S.season_empty_why);
    return section(title, S.season_closed_ago({ ago: formatAgo(ctx.now(), b.closedAt) }), row + body);
  }

  /** The closed seasons, newest first: one card each, with the Honour that
   *  season paid (R1599). A card is one item of the list, so it keeps its
   *  box (R1945). */
  function historyCards(): string {
    const cell = ctx.data.cell<BoardSnapshot[]>(
      'boards:history', TTL_ON_OPEN, () => getBoardHistory());
    const list = cell.value;
    if (list === undefined) return pending(cell);
    if (list.length === 0) return empty(S.history_empty, S.history_empty_why);
    return list.map((s) => {
      const names = snapshotNames(s.payload);
      const body = names.length === 0
        ? empty(S.snapshot_empty, S.snapshot_empty_why)
        : names.map((n) => ladderRow(
          ordinal(n.rank), n.name, S.honour_value({ honour: groupDigits(n.honour) }),
          rowCls(false, n.rank), false,
        )).join('');
      return card(
        S.snapshot_label({ board: s.board.toUpperCase(), season: s.season }), '▤',
        S.snapshot_more({ mode: s.mode.toUpperCase(), ago: formatAgo(ctx.now(), s.takenAt) }), body,
      );
    }).join('');
  }

  /** SEASONS: the season table of the account's own seat and HISTORY, two
   *  sections. An account with no seat sees HISTORY alone, as the one
   *  section of the page, so it draws with no sub-header (R1945). */
  function seasonsBoard(): string {
    const own = seasonCard();
    const history = historyCards();
    return own === '' ? plain(history) : own + section(S.card_history_title, '', history);
  }

  /** The memorial pool (numbers.md §14.1, R1243), newest season first:
   *  one card per name, with the origin world and season, the score and
   *  how many maps the meta server served the name to. */
  function memorialCards(): string {
    const cell = ctx.data.cell<MemorialEntry[]>(
      'boards:memorials', TTL_ON_OPEN, () => getBoardMemorials(CHROME_MEMORIALS_SHOWN));
    const list = cell.value;
    if (list === undefined) return plain(pending(cell));
    if (list.length === 0) return plain(empty(S.memorials_empty, S.memorials_empty_why));
    return plain(lede(S.memorials_lede) + list.map((m) => card(
      m.name, '✦', S.memorial_label({ world: m.world, season: m.season }),
      rows([[S.memorial_score({ score: groupDigits(m.score) }), S.memorial_shown({ n: m.shown })]]),
    )).join(''));
  }

  /** What each board draws. SEASONS is the season table of the account's
   *  own seats over the closed seasons of every world. */
  const BOARDS: Record<Board, () => string> = {
    honour: honourBoard,
    clans: clanCard,
    seasons: seasonsBoard,
    memorials: memorialCards,
    achievements: achievementCard,
    ladder: ladderBoard,
  };

  // --- the section shell (§16.3, §16.16) ------------------------------------

  /** The Honour figure of a line or of a gap. */
  const honourFig = (v: number): string => S.honour_value({ honour: groupDigits(v) });

  /**
   * What the shell knows of one board, from a PEEK at its read: a summary
   * and the YOU column never start a read, so a visit reads the boards
   * the reader opens and no more. The page of the open board reads its
   * cell first, so the peek of that board sees the same answer.
   */
  function view(b: Board): BoardView | undefined {
    const me = ctx.me();
    switch (b) {
      case 'honour': {
        const cell = ctx.data.peek<HonourBoard>(`honour:${honourMode}`);
        return {
          cell, fmt: honourFig, own: honourFig(ownHonour(me, honourMode)),
          place: placeOr(places()?.honour[honourMode].place),
          lines: cell.value?.rows.map((r) => ({
            place: r.rank, name: r.name, value: r.honour, self: r.accountId === me.account.id,
          })),
        };
      }
      case 'clans': {
        const cell = ctx.data.peek<ClanHonourBoard>('clans');
        const mine = me.clan ?? null;
        if (mine === null) {
          return { cell, note: S.you_no_clan, press: ctlRow(button(S.clans_empty_press, { act: 'tab', data: tabData('clan', 'find') }, { variant: 'primary' })) };
        }
        const own = places()?.clan;
        return {
          cell, fmt: honourFig, own: honourFig(mine.honourClanwar ?? 0),
          place: own?.clanId === mine.id ? placeOr(own.place) : undefined,
          lines: cell.value?.rows.map((r) => ({
            place: r.rank, name: S.clan_row({ tag: r.tag, name: r.name }), value: r.honour, self: r.clanId === mine.id,
          })),
        };
      }
      case 'seasons': {
        const seat = chosenSeat();
        if (seat === undefined) return undefined;
        const cell = ctx.data.peek<SeasonBoard>(`season:${seasonKey(seat)}`);
        const own = places()?.seasons.find((s) => s.worldId === seat.worldId && s.season === seat.season);
        if (cell.value === undefined && (isNotFound(cell.error) || own?.closed === false)) {
          return { cell, note: S.season_open_why };
        }
        return {
          cell, fmt: groupDigits, place: own?.closed ? placeOr(own.place) : undefined,
          lines: cell.value?.rows.map((r) => ({
            place: r.rank, name: r.name, value: r.score, self: r.accountId === me.account.id,
          })),
        };
      }
      case 'memorials':
        // The pool holds the winners of every world, and no read states
        // the reader's place on it.
        return undefined;
      case 'achievements': {
        const cell = ctx.data.peek<AchievementBoard>(`achboard:${achBoard}:${achTier}`);
        return {
          cell, fmt: (v) => A.record_points({ points: groupDigits(v) }),
          lines: cell.value?.rows.map((r) => ({ place: r.place, name: r.name, value: r.points, self: r.self })),
        };
      }
      case 'ladder': {
        const cell = ctx.data.peek<LadderList>('ladder:list');
        const l = cell.value;
        if (l?.me?.guest || places()?.ladder.guest) return { cell, note: L.guest_line };
        return {
          cell, place: l?.me === null || l?.me === undefined ? placeOr(places()?.ladder.place) : l.me.place,
          lines: l?.rows.map((r) => ({ place: r.place, name: r.name, value: r.ladder, self: r.self })),
        };
      }
    }
  }

  /** The tier of the reader on the arcade ladder, from the read of R1907,
   *  else from the list, else from the ladder card of RECORD where that has
   *  answered; '' for a guest or where none has. */
  function ladderTier(): string {
    const own = places()?.ladder;
    if (own?.guest) return '';
    if (own !== undefined && own.tier > 0) return tierName(own.tier);
    const me = ctx.data.peek<LadderList>('ladder:list').value?.me;
    if (me !== null && me !== undefined) return me.guest ? '' : tierName(ladderTierOf(me.ladder));
    const c = ctx.data.peek<LadderCard>('ladder:me').value;
    return c === undefined || c.guest ? '' : tierName(ladderTierOf(c.ladder));
  }

  /**
   * The summary of one board: the reader's place where the board has
   * answered with the own line, else the figure the account carries, else
   * nothing (§16.16 "The summary rule").
   */
  function summary(b: Board): string {
    const v = view(b);
    const st = v?.lines === undefined ? undefined : standingOf(v.lines, 0);
    const me = ctx.me();
    switch (b) {
      case 'honour': {
        const value = st !== undefined && v?.fmt !== undefined
          ? S.sum_place({ place: ordinal(st.line.place), value: v.fmt(st.line.value) })
          : v?.place !== undefined && v.own !== undefined
            ? S.sum_place({ place: ordinal(v.place), value: v.own })
            : v?.own ?? '';
        if (honourMode === 'core') return value;
        return S.sum_mode({ mode: { event: S.toggle_event, clanwar: S.toggle_clanwar }[honourMode], value });
      }
      case 'clans': {
        const mine = me.clan ?? null;
        if (mine === null) return S.sum_no_clan;
        const place = st?.line.place ?? v?.place;
        if (place === undefined) return S.sum_clan_tag({ tag: mine.tag });
        return S.sum_clan({ tag: mine.tag, place: ordinal(place) });
      }
      case 'seasons': {
        const seat = chosenSeat();
        if (seat === undefined) return '';
        const place = st?.line.place ?? v?.place;
        if (place !== undefined) return S.sum_season({ season: seat.season, place: ordinal(place) });
        if (v?.note !== undefined) return S.sum_season_open({ season: seat.season });
        return S.season_label({ world: seat.world, season: seat.season });
      }
      case 'memorials':
        return '';
      case 'achievements':
        return st === undefined || v?.fmt === undefined ? ''
          : S.sum_place({ place: ordinal(st.line.place), value: v.fmt(st.line.value) });
      case 'ladder': {
        const tier = ladderTier();
        const place = st?.line.place ?? v?.place ?? 0;
        if (tier === '' || place <= 0) return tier;
        return S.sum_ladder({ tier, place: ordinal(place) });
      }
    }
  }

  /** The six sections as the rail and the tiles draw them. */
  function sections(): ShellSection[] {
    const one = (id: Board, glyph: string, name: string): ShellSection => ({ id, glyph, name, sum: summary(id) });
    return [
      one('honour', '✦', S.board_honour),
      one('clans', '⬢', S.board_clans),
      one('seasons', '▤', S.board_seasons),
      one('memorials', '❦', S.board_memorials),
      one('achievements', '◈', A.ranks_pill),
      one('ladder', '▼', L.ranks_pill),
    ];
  }

  /** One line of the YOU column: the place, the name and the value. */
  function nearLine(l: BoardLine, fmt: ((v: number) => string) | undefined): string {
    return `<div class="ynear${l.self ? ' me' : ''}"><span class="n">${esc(ordinal(l.place))}</span>`
      + `<span class="nm">${esc(l.name)}</span><span class="v">${fmt === undefined ? '' : esc(fmt(l.value))}</span></div>`;
  }

  /** YOU ON THIS BOARD: the place in the large face over the value, the
   *  places on each side of the own line, the gap to the next place, and
   *  ▸ RECORD. A board that has not answered states no figure. */
  function youHtml(v: BoardView): string {
    const record = ctlRow(button(S.to_record, { act: 'tab', data: tabData('record', 'overview') }, { variant: 'primary' }));
    if (v.note !== undefined) return lede(v.note) + (v.press ?? '');
    if (v.lines === undefined) return pending(v.cell);
    const st = standingOf(v.lines, HUB_SECTIONS.ranksNearRows);
    const place = st?.line.place ?? v.place ?? 0;
    const value = st !== undefined && v.fmt !== undefined ? v.fmt(st.line.value) : v.own ?? '';
    const big = `<div class="ybig"><b>${esc(place > 0 ? ordinal(place) : S.unranked)}</b>`
      + (value === '' ? '' : `<small>${esc(value)}</small>`) + '</div>';
    // An empty board holds no row to be off (R2047).
    const off = v.lines.length === 0 ? S.you_board_empty : S.you_off_page;
    // An empty board has one next step, the press of its empty line, and
    // no ▸ RECORD beside it (R2146).
    if (v.lines.length === 0) return big + lede(off);
    if (st === undefined) return big + lede(place > 0 ? S.you_below : off) + record;
    const near = st.near.map((l) => nearLine(l, v.fmt)).join('');
    const next: Row[] = st.gap !== undefined && v.fmt !== undefined
      ? [[S.you_next, S.gap_value({ value: v.fmt(st.gap) })]] : [];
    return big + near + (st.gap === undefined ? lede(S.you_first) : rows(next)) + record;
  }

  /** True where the page of the board draws a row of the reader's own:
   *  the own line, or the row the board pins for a reader off the page. */
  function drawsOwnRow(v: BoardView): boolean {
    // An empty board draws no row to scroll to, so it pins no foot (R2146).
    if (v.lines === undefined || v.lines.length === 0 || v.note !== undefined) return false;
    return standingOf(v.lines, 0) !== undefined || v.own !== undefined || v.place !== undefined;
  }

  /** The pinned foot: YOU, the summary, and the press that scrolls the
   *  board to the reader's row. */
  function pinHtml(sum: string): string {
    return `<button type="button" class="ypin press" data-act="${MY_ROW}">`
      + `<span class="k">${esc(S.you_card)}</span><b>${esc(sum === '' ? S.unranked : sum)}</b>`
      + `<span class="a">${esc(S.show_row)}</span></button>`;
  }

  /** The companion of one board, or none for a board with no place of
   *  the reader (MEMORIALS, and SEASONS for an account with no seat). */
  function you(b: Board): Companion | undefined {
    const v = view(b);
    if (v === undefined) return undefined;
    const pin = drawsOwnRow(v);
    return {
      label: S.you_label, html: youHtml(v),
      fold: pin ? 'foot' : 'none', narrow: pin ? 'foot' : 'none',
      folded: pin ? pinHtml(summary(b)) : undefined,
    };
  }

  /** The YOU card under the tiles of a telephone: the summaries of the
   *  three lifetime boards, where each has one. */
  function rootYou(): string {
    const list: Row[] = ([
      [S.board_honour, summary('honour')], [S.board_clans, summary('clans')], [L.ranks_pill, summary('ladder')],
    ] as Row[]).filter(([, v]) => v !== '');
    return list.length === 0 ? '' : section(S.you_card, '', rows(list));
  }

  /** The page of one board. The board reads its cell before the companion
   *  peeks at it. */
  function sectionPage(b: Board): ShellPage {
    const body = col(BOARDS[b]());
    return { body, companion: you(b) };
  }

  /** Takes one pick: the cue, the new value and a redraw. A press on the
   *  value already picked does nothing. */
  function take<T>(have: T, want: T, cue: SfxId, set: (v: T) => void): void {
    if (want === have) return;
    ctx.play(cue);
    set(want);
    ctx.refresh();
  }

  return {
    key: 'ranks',

    open(): void {
      nav.open();
    },

    close(): void {
      nav.close();
    },

    // A press in another section opens RANKS on one board: the ▸ RANKS
    // press of the RECORD cards ARCADE LADDER and ACHIEVEMENTS goes through
    // `ctx.go('ranks', board)` (R1737, R1738). The hub calls `open` after
    // it; a press while RANKS stands open opens the board at once, as
    // `SectionNav.ask` does for every panel (R2237).
    openPage(p: PageKey): void {
      const want = readBoard(p);
      if (want === undefined) return;
      ask(want);
    },

    openSection(word: string): void {
      ask(word);
    },

    html(): string {
      // The one read of the reader's places (R1907): on the first open of
      // RANKS, and again when the tiles show after its cache life.
      ctx.data.cell<MyPlaces>(PLACES_KEY, TTL_ON_OPEN, () => getMyPlaces());
      const wide = ctx.wide();
      const open = nav.shown(wide);
      // The page reads first, so the summaries peek at the answer it has.
      const pageSpec = open === null ? { body: '' } : sectionPage(open);
      return shellHtml({
        title: S.title,
        heading: h1(S.title, S.subtitle),
        go: GO,
        back: BACK,
        sections: sections(),
        open,
        wide,
        page: pageSpec,
        rootFoot: rootYou(),
      });
    },

    wire(root: HTMLElement): void {
      nav.scroll(root);
    },

    act(action: string, el: HTMLElement): void {
      if (shellPress(nav, ctx, SHELL, action, el)) return;
      const d = el.dataset;
      switch (action) {
        case MY_ROW: {
          // The pinned foot scrolls the board to the reader's row: the own
          // line, or the row the board pins under the list.
          const host = el.closest('.hs-pane, .hs-page');
          const row = host?.querySelector<HTMLElement>('.hs-body .row.self, .hs-body .lrk.me');
          if (row === null || row === undefined) return;
          ctx.play('sfx-menu-press');
          row.scrollIntoView?.({ block: 'center' });
          return;
        }
        case 'honourmode':
          take(honourMode, (d.id ?? 'core') as HonourMode, 'sfx-menu-toggle',
            (v) => { honourMode = v; });
          return;
        case 'season':
          take(season, d.id ?? '', 'sfx-menu-press', (v) => { season = v; });
          return;
        case 'achboard':
          take(achBoard, (d.id ?? 'life') as AchievementBoardKind, 'sfx-menu-toggle',
            (v) => { achBoard = v; });
          return;
        case 'achtier':
          take(achTier, (d.id ?? 'premium') as AchievementTier, 'sfx-menu-toggle',
            (v) => { achTier = v; });
          return;
        default:
      }
    },
  };
}
